import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Center,
  Group,
  Loader,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconLock, IconPencil, IconPlus, IconTrash } from '@tabler/icons-react';
import { PageHeader } from '../../shared/components/PageHeader';
import { ResponsiveTable } from '../../shared/components/ResponsiveTable';
import type { DataColumn } from '../../shared/components/ResponsiveTable';
import { ApiError } from '../../shared/api/problem';
import { usePermissions } from '../auth/usePermissions';
import { deleteGroup, listGroups, type GroupDto } from './api';
import { GroupPermissionsModal } from './GroupPermissionsModal';

/**
 * The groups a tenant grants access through.
 *
 * Access here is permission-based, and a group is what permissions attach to: a user is given one
 * or more groups and the union is what they can do. That is legacy's model — tblWebGroup,
 * tblWebGroupPermissionMap, tblWebEmployeeGroupMap — and until tenant script 047 the permission
 * map had 105 seeded rows and no writer, so nothing here could be changed at all.
 *
 * The four seeded groups are locked and sort first. Everything below them is the tenant's own.
 */

/**
 * The server's own sentence, not a shrug.
 *
 * Every refusal from the user and group endpoints names what went wrong — which group is locked,
 * how many members block a delete, that a script is missing. These two screens were the only ones
 * showing a generic "Could not load", which on 2026-08-18 hid a stale API deployment behind four
 * identical words while the database was fine. A 404 or a 403 says something too, so the status is
 * included when the body carries no detail.
 */
function describeLoadFailure(err: unknown, fallback: string): string {
  if (!(err instanceof ApiError)) return fallback;
  const detail = err.problem.detail ?? err.problem.title;
  return detail ? `${fallback} ${detail}` : `${fallback} (HTTP ${err.status})`;
}

export function GroupsRoute() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const permissions = usePermissions();

  const [editing, setEditing] = useState<GroupDto | null>(null);
  const [creating, setCreating] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const canManage = permissions.has('tenant:manage_users');

  const groups = useQuery({
    queryKey: ['groups'] as const,
    queryFn: ({ signal }) => listGroups(signal),
    enabled: canManage,
  });

  const remove = useMutation({
    mutationFn: (groupId: string) => deleteGroup(groupId),
    onSuccess: () => {
      notifications.show({ message: t('groups.deleted'), color: 'green' });
      setActionError(null);
      void queryClient.invalidateQueries({ queryKey: ['groups'] });
    },
    onError: (err) => {
      // The refusals name the group and the member count, and say to deactivate instead.
      setActionError(
        err instanceof ApiError
          ? (err.problem.detail ?? err.problem.title ?? t('groups.errors.deleteFailed'))
          : t('groups.errors.deleteFailed'),
      );
    },
  });

  const columns: DataColumn<GroupDto>[] = [
    {
      key: 'name',
      label: t('groups.name'),
      card: 'title',
      render: (g) => (
        <Group gap={6} wrap="nowrap">
          <Text size="sm">{g.displayName}</Text>
          {/* An icon rather than a badge: it is true of the four seeded groups and a column of
              identical badges is noise — the same call the transaction code screen makes. */}
          {g.isLocked && (
            <Tooltip label={t('groups.lockedHint')}>
              <IconLock size={13} aria-label={t('groups.locked')} />
            </Tooltip>
          )}
        </Group>
      ),
    },
    { key: 'code', label: t('groups.code'), card: 'subtitle', render: (g) => g.groupCode },
    {
      key: 'permissions',
      label: t('groups.permissions'),
      align: 'right',
      card: 'aside',
      // A group granting nothing is the interesting case: somebody is in it expecting access.
      render: (g) =>
        g.permissionCount === 0 ? (
          <Badge size="sm" variant="light" color="yellow">
            {t('groups.grantsNothing')}
          </Badge>
        ) : (
          g.permissionCount
        ),
    },
    {
      key: 'members',
      label: t('groups.members'),
      align: 'right',
      render: (g) => g.memberCount,
    },
    {
      key: 'status',
      label: t('groups.status'),
      render: (g) =>
        g.isActive ? (
          <Text size="sm" c="dimmed">
            {t('groups.active')}
          </Text>
        ) : (
          <Badge size="sm" variant="light" color="gray">
            {t('groups.inactive')}
          </Badge>
        ),
    },
  ];

  if (!canManage) {
    return (
      <>
        <PageHeader title={t('nav.accessGroups')} />
        <Alert color="yellow" variant="light" icon={<IconAlertCircle size={16} />}>
          {t('groups.notPermitted')}
        </Alert>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t('nav.accessGroups')}
        actions={
          <Button color="invetrak" leftSection={<IconPlus size={16} />} onClick={() => setCreating(true)}>
            {t('groups.new')}
          </Button>
        }
      />

      <Stack gap="md">
        <Alert color="blue" variant="light" icon={<IconAlertCircle size={16} />}>
          {t('groups.intro')}
        </Alert>

        {actionError && (
          <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
            {actionError}
          </Alert>
        )}

        {groups.isLoading ? (
          <Center py="xl">
            <Loader color="invetrak" />
          </Center>
        ) : groups.isError ? (
          <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
            {describeLoadFailure(groups.error, t('groups.errors.listFailed'))}
          </Alert>
        ) : (
          <ResponsiveTable
            rows={groups.data ?? []}
            columns={columns}
            rowKey={(g) => g.groupId}
            minWidth={760}
            actionsWidth={90}
            striped
            highlightOnHover
            actions={(g) => (
              <>
                <Tooltip label={g.isLocked ? t('groups.viewAria', { name: g.displayName }) : t('groups.editAria', { name: g.displayName })}>
                  <ActionIcon
                    variant="subtle"
                    color="invetrak"
                    aria-label={g.isLocked ? t('groups.viewAria', { name: g.displayName }) : t('groups.editAria', { name: g.displayName })}
                    onClick={() => setEditing(g)}
                  >
                    <IconPencil size={16} />
                  </ActionIcon>
                </Tooltip>
                {/* Disabled rather than hidden for a locked group, with the reason on the
                    tooltip: a missing button is a puzzle, a disabled one is an answer. */}
                <Tooltip
                  label={
                    g.isLocked
                      ? t('groups.lockedHint')
                      : g.memberCount > 0
                        ? t('groups.hasMembersHint')
                        : t('groups.deleteAria', { name: g.displayName })
                  }
                >
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    aria-label={t('groups.deleteAria', { name: g.displayName })}
                    disabled={g.isLocked || g.memberCount > 0}
                    onClick={() => remove.mutate(g.groupId)}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Tooltip>
              </>
            )}
          />
        )}
      </Stack>

      <GroupPermissionsModal
        group={editing}
        creating={creating}
        onClose={() => {
          setEditing(null);
          setCreating(false);
        }}
        onSaved={() => void queryClient.invalidateQueries({ queryKey: ['groups'] })}
      />
    </>
  );
}
